import { SystemSkillBadge } from "./SystemSkillBadge";
import { RestoreSystemSkillDialog } from "./RestoreSystemSkillDialog";
import { Editor } from "@monaco-editor/react";

import { PermissionTooltip } from "@/components/PermissionGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingButton } from "@/components/ui/loading-button";
import { Switch } from "@/components/ui/switch";
import { useTheme } from "@/contexts/useTheme";
import { usePageTitle } from "@/hooks/usePageTitle";

import { SkillDeleteDialog } from "./SkillDeleteDialog";
import { AGENT_RESOURCES_COPY } from "./agentResourceCopy";
import { FactorySettingsPageFrame } from "./FactorySettingsCard";
import { useSkillEditorPage } from "./useSkillEditorPage";

export function FactorySettingsSkillEditorPage() {
  const editor = useSkillEditorPage();
  const { resolvedTheme } = useTheme();
  usePageTitle([
    editor.isCreate ? AGENT_RESOURCES_COPY.addSkill : editor.name || AGENT_RESOURCES_COPY.editSkill,
    AGENT_RESOURCES_COPY.agentTitle,
    editor.factory?.name ?? "Workspace",
  ]);

  if (!editor.isCreate && !editor.skillsLoading && !editor.resource) {
    return (
      <FactorySettingsPageFrame title={AGENT_RESOURCES_COPY.editSkill} wide>
        <p className="text-[13px] text-destructive">{AGENT_RESOURCES_COPY.skillNotFound}</p>
      </FactorySettingsPageFrame>
    );
  }

  return (
    <FactorySettingsPageFrame
      title={editor.isCreate ? AGENT_RESOURCES_COPY.addSkill : AGENT_RESOURCES_COPY.editSkill}
      subtitle={AGENT_RESOURCES_COPY.skillDialogDescription}
      wide
      actions={<SkillEditorActions editor={editor} />}
    >
      {editor.resource?.systemSkill ? (
        <div className="mb-4 flex items-center gap-2">
          <SystemSkillBadge state={editor.resource.systemSkill.state} />
          <p className="text-[13px] text-muted-foreground">{AGENT_RESOURCES_COPY.systemSkillProtected}</p>
        </div>
      ) : null}
      <SkillEditorFields
        systemSkill={Boolean(editor.resource?.systemSkill)}
        name={editor.name}
        markdown={editor.markdown}
        command={editor.command}
        commandName={editor.commandName}
        nameError={editor.nameError}
        markdownError={editor.markdownError}
        resolvedTheme={resolvedTheme}
        onNameChange={editor.setName}
        onMarkdownChange={editor.setMarkdown}
      />
      <RestoreSystemSkillDialog
        open={editor.pendingRestore}
        isRestoring={editor.isRestoring}
        onClose={() => editor.setPendingRestore(false)}
        onConfirm={editor.confirmRestore}
      />
      {editor.pendingDelete && editor.resource ? (
        <SkillDeleteDialog
          resource={editor.resource}
          canDelete={editor.canUpdate}
          isDeleting={editor.isDeleting}
          onClose={() => editor.setPendingDelete(false)}
          onConfirm={editor.confirmDelete}
        />
      ) : null}
    </FactorySettingsPageFrame>
  );
}

function SkillEditorActions({ editor }: { editor: ReturnType<typeof useSkillEditorPage> }) {
  const displayName = editor.name.trim() || AGENT_RESOURCES_COPY.unnamedSkill;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {editor.canRestore ? (
        <Button
          type="button"
          variant="outline"
          disabled={!editor.canUpdate || editor.isSaving || editor.isRestoring}
          onClick={() => editor.setPendingRestore(true)}
        >
          {AGENT_RESOURCES_COPY.restoreSkill}
        </Button>
      ) : null}
      {!editor.isCreate && editor.resource ? (
        <div className="mr-1 flex items-center gap-3">
          <span className="text-[12px] text-muted-foreground">Enabled</span>
          <Switch
            checked={editor.resource.enabled !== false}
            disabled={!editor.canUpdate || Boolean(editor.resource?.systemSkill)}
            onCheckedChange={editor.toggleEnabled}
            aria-label={`Enable ${displayName}`}
            data-testid="skill-editor-enabled"
          />
        </div>
      ) : null}
      {!editor.isCreate ? (
        <Button
          type="button"
          variant="outline"
          onClick={() => editor.setPendingDelete(true)}
          disabled={!editor.canUpdate}
        >
          {AGENT_RESOURCES_COPY.delete}
        </Button>
      ) : null}
      <Button type="button" variant="outline" onClick={editor.navigateToList}>
        {AGENT_RESOURCES_COPY.cancel}
      </Button>
      <PermissionTooltip allowed={editor.canUpdate} message={AGENT_RESOURCES_COPY.noUpdatePermission}>
        <LoadingButton
          type="button"
          onClick={() => void editor.handleSave()}
          loading={editor.isSaving}
          disabled={!editor.canUpdate || editor.isRestoring}
          data-testid="agent-resource-skill-save"
        >
          {editor.isCreate ? AGENT_RESOURCES_COPY.addSkill : AGENT_RESOURCES_COPY.saveSkill}
        </LoadingButton>
      </PermissionTooltip>
    </div>
  );
}

function SkillEditorFields({
  systemSkill,
  name,
  markdown,
  command,
  commandName,
  nameError,
  markdownError,
  resolvedTheme,
  onNameChange,
  onMarkdownChange,
}: {
  systemSkill: boolean;
  name: string;
  markdown: string;
  command: string;
  commandName: string;
  nameError: string;
  markdownError: string;
  resolvedTheme: string;
  onNameChange: (value: string) => void;
  onMarkdownChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-4" data-testid="factory-settings-skill-editor">
      <SkillNameFields
        systemSkill={systemSkill}
        name={name}
        command={command}
        commandName={commandName}
        nameError={nameError}
        onNameChange={onNameChange}
      />
      <div className="flex flex-col gap-1.5">
        <Label>{AGENT_RESOURCES_COPY.markdownLabel}</Label>
        <div
          className="h-[70vh] min-h-[480px] overflow-hidden rounded-lg border border-border"
          data-testid="agent-resource-skill-markdown"
        >
          <Editor
            height="100%"
            defaultLanguage="markdown"
            language="markdown"
            value={markdown}
            theme={resolvedTheme === "dark" ? "vs-dark" : "vs"}
            onChange={(next) => onMarkdownChange(next ?? "")}
            options={{
              minimap: { enabled: false },
              wordWrap: "on",
              fontSize: 13,
              scrollBeyondLastLine: false,
              automaticLayout: true,
            }}
          />
        </div>
        <p className="text-[12px] text-muted-foreground">{AGENT_RESOURCES_COPY.markdownHelper}</p>
        {markdownError ? <p className="text-[12px] text-destructive">{markdownError}</p> : null}
      </div>
    </div>
  );
}

function SkillNameFields({
  systemSkill,
  name,
  command,
  commandName,
  nameError,
  onNameChange,
}: {
  systemSkill: boolean;
  name: string;
  command: string;
  commandName: string;
  nameError: string;
  onNameChange: (value: string) => void;
}) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="skill-editor-name">{AGENT_RESOURCES_COPY.nameLabel}</Label>
        <Input
          id="skill-editor-name"
          disabled={systemSkill}
          data-testid="agent-resource-skill-name"
          value={name}
          onChange={(event) => onNameChange(event.target.value)}
          placeholder="Review copy"
          autoComplete="off"
        />
        <p className="text-[12px] text-muted-foreground">{AGENT_RESOURCES_COPY.skillNameHelper}</p>
        {nameError ? <p className="text-[12px] text-destructive">{nameError}</p> : null}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="skill-editor-command">{AGENT_RESOURCES_COPY.skillCommandLabel}</Label>
        <Input id="skill-editor-command" data-testid="agent-resource-skill-command" value={command} readOnly />
        <p className="text-[12px] text-muted-foreground">
          {commandName
            ? AGENT_RESOURCES_COPY.skillCommandHelper(commandName)
            : AGENT_RESOURCES_COPY.skillCommandEmptyHelper}
        </p>
      </div>
    </div>
  );
}
